import { FC } from 'react';
import { IconLongArrowRightBold, Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Log } from '@common/log-manager';
import { useInitLikedProducts } from '@widgets/like-product';
import { VerticalProductCardWithItem } from '@common/product-card';
import {
  convertProductListToIdList,
  ImageBannerListComponentType,
  useProductCardEvents,
} from '@domains/storehome-common';

import { useImageBannerListService } from '../services/useImageBannerListService';

interface ImageBannerListCollectionProps {
  log?: Partial<Log>;
  component: ImageBannerListComponentType;
}

export const ImageBannerListContainer: FC<React.PropsWithChildren<ImageBannerListCollectionProps>> = ({
  log,
  component: image_banner_list_component,
}) => {
  const { title, image_url, landing_url, action_button, product_list } = image_banner_list_component;
  const { product_card_list, moveToCollectionPage } = useImageBannerListService({ log, product_list });

  const { onClick, onImpression, onLikeClick } = useProductCardEvents();

  useInitLikedProducts({
    data: product_card_list,
    convert: convertProductListToIdList,
  });

  return (
    <Root>
      <Top onClick={(event) => moveToCollectionPage(event, action_button, landing_url)}>
        <Image src={image_url} alt='image_banner_list main image' />
        <Dimmed />
        <TitleWrapper>
          <Text variant='Head_20_Multiline_Bold' color='staticWhite' css={TitleCss}>
            {title}
          </Text>
          <IconWrapper>
            <IconLongArrowRightBold size={20} color='staticWhite' />
          </IconWrapper>
        </TitleWrapper>
      </Top>
      <List>
        {product_card_list.map((item, index) => (
          <ProductCardWrapper key={item.catalog_product_id}>
            <VerticalProductCardWithItem
              item={item}
              size='small'
              onClick={(item, event) => {
                return onClick(event, item, index, log && { ...log, object_idx: index });
              }}
              onLikeClick={(isLiked, item) => {
                return onLikeClick(isLiked, item, index, log && { ...log, object_idx: index });
              }}
              onImpression={(item) => {
                return onImpression(item, index, log && { ...log, object_idx: index });
              }}
            />
          </ProductCardWrapper>
        ))}
      </List>
    </Root>
  );
};

const TitleCss = css`
  white-space: pre-line;
  overflow: hidden;
  display: -webkit-box;
  max-height: 56px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const IconWrapper = styled.div`
  padding: 4px 10.5px;
  border-radius: 87.5px;
  background-color: ${vars.color.onImageBlackOpacity30};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Root = styled.section`
  padding-bottom: 20px;
`;

const Top = styled.div`
  cursor: pointer;
  position: relative;
  margin: 0 16px;
  overflow: hidden;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  gap: 4px;
  justify-content: space-between;
  z-index: 2;
`;

const Image = styled.img`
  width: 100%;
  object-fit: cover;
  object-position: center;
  aspect-ratio: 1.472103;
`;

const Dimmed = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 49.76%, rgba(0, 0, 0, 0.3) 100%);
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const List = styled.div`
  display: flex;
  padding: 2px 16px 0;
  justify-content: space-between;
  gap: 2px;
`;

const ProductCardWrapper = styled.div`
  flex: 1;
  width: 0;
`;
